import { Link } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { PriceListDialog } from "@/components/PriceListDialog";
import { Upload, Mail, FileClock, ArrowRight, Zap, Tag, FileText, Plane, Package, ClipboardList, FileUser, Image, StickyNote, Sparkles, ExternalLink } from "lucide-react";

export default function Landing() {
  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-40 bg-white/70 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-3">
            <PriceListDialog trigger={<button data-testid="nav-pricing" className="text-sm font-semibold text-slate-700 hover:text-ink transition-colors px-3 py-2 hidden sm:inline-flex items-center gap-1.5"><Tag size={14} /> Pricing</button>} />
            <Link to="/login" data-testid="nav-login" className="text-sm font-semibold text-slate-700 hover:text-ink transition-colors px-3 py-2">
              View my jobs
            </Link>
            <Link to="/admin/login" data-testid="nav-admin" className="text-sm font-semibold text-white bg-ink hover:bg-black transition-colors px-4 py-2 rounded-md">
              Staff login
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 md:px-10">
        <section className="py-16 md:py-24 grid lg:grid-cols-2 gap-12 items-center">
          <div className="animate-fade-up">
            <span className="uppercase text-xs tracking-[0.2em] font-medium text-brand">Borrow A Geek · Print Services</span>
            <h1 className="font-heading font-black tracking-tight text-4xl sm:text-5xl lg:text-6xl mt-4 leading-[1.05] text-ink">
              Send us your files.<br />We'll do the printing.
            </h1>
            <p className="text-base md:text-lg text-slate-600 mt-6 max-w-lg leading-relaxed">
              Upload a document, tell us how you'd like it printed, and get an instant price. Track every job from submission to pick-up.
            </p>
            <div className="flex flex-wrap gap-4 mt-8">
              <Link to="/submit" data-testid="cta-submit" className="inline-flex items-center gap-2 bg-brand hover:bg-brand-hover text-white font-semibold px-6 py-3.5 rounded-lg transition-transform hover:-translate-y-px">
                <Upload size={18} /> Submit a print job
              </Link>
              <Link to="/login" data-testid="cta-track" className="inline-flex items-center gap-2 border-2 border-ink text-ink font-semibold px-6 py-3.5 rounded-lg hover:bg-ink hover:text-white transition-colors">
                <FileClock size={18} /> Track my orders
              </Link>
              <PriceListDialog />
            </div>
          </div>
          <div className="relative animate-fade-up" style={{ animationDelay: "0.1s" }}>
            <img
              src="https://images.unsplash.com/photo-1562240020-ce31ccb0fa7d?crop=entropy&cs=srgb&fm=jpg&q=85&w=900"
              alt="Stack of colourful printing paper"
              className="rounded-2xl border border-black/5 shadow-xl w-full object-cover max-h-[460px]"
            />
            <div className="absolute -bottom-5 -left-5 bg-white rounded-xl border border-slate-200 shadow-lg px-5 py-4 hidden sm:block">
              <div className="flex items-center gap-2 text-brand"><Zap size={16} /><span className="font-heading font-bold text-ink">Instant quote</span></div>
              <div className="text-xs text-slate-500 mt-1">Priced by size, colour & paper</div>
            </div>
          </div>
        </section>

        <section className="grid md:grid-cols-3 gap-6 pb-24">
          {[
            { icon: Upload, t: "Upload on the web", d: "Drag in your file and answer six quick questions to submit instantly." },
            { icon: Mail, t: "Or just email us", d: "Send your file by email — we'll reply with a link to gather the details." },
            { icon: FileClock, t: "Track & pick up", d: "Get a confirmation with a magic link to follow your job's status." },
          ].map((f, i) => (
            <div key={i} className="bg-white rounded-xl border border-slate-200 p-6 sm:p-8 hover:-translate-y-1 transition-transform">
              <span className="grid place-items-center w-11 h-11 rounded-lg bg-orange-50 text-brand mb-4"><f.icon size={20} /></span>
              <h3 className="font-heading font-bold text-lg text-ink">{f.t}</h3>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">{f.d}</p>
            </div>
          ))}
        </section>

        <section className="pb-16 md:pb-24">
          <div className="max-w-2xl">
            <span className="uppercase text-xs tracking-[0.2em] font-medium text-brand">What we print</span>
            <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight text-ink mt-3">Everyday documents, printed fast</h2>
            <p className="text-base text-slate-600 mt-3 leading-relaxed">Bring us just about anything you need on paper. Here are some of the most popular jobs we handle every day.</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8" data-testid="print-types">
            {[
              { icon: FileText, t: "PDF documents", d: "Reports, contracts, forms" },
              { icon: Plane, t: "Boarding passes", d: "Travel-ready, in minutes" },
              { icon: Package, t: "Shipping labels", d: "Clear, scannable prints" },
              { icon: ClipboardList, t: "Applications", d: "Forms & paperwork" },
              { icon: FileUser, t: "CVs & résumés", d: "Sharp, professional copies" },
              { icon: Image, t: "Posters", d: "A3 poster-size prints" },
              { icon: StickyNote, t: "Flyers", d: "Handouts & notices" },
              { icon: Sparkles, t: "…and more", d: "Just ask — we can help" },
            ].map((f, i) => (
              <div key={i} className="bg-white rounded-xl border border-slate-200 p-5 hover:-translate-y-1 hover:border-brand transition-transform" data-testid={`print-type-${i}`}>
                <span className="grid place-items-center w-10 h-10 rounded-lg bg-orange-50 text-brand mb-3"><f.icon size={18} /></span>
                <h3 className="font-heading font-bold text-base text-ink">{f.t}</h3>
                <p className="text-xs text-slate-500 mt-1">{f.d}</p>
              </div>
            ))}
          </div>

          <div className="mt-8 rounded-2xl border border-slate-200 bg-[#F3F4F6] p-6 sm:p-8 flex flex-col md:flex-row md:items-center gap-5" data-testid="full-bleed-note">
            <div className="flex-1">
              <div className="flex items-center gap-2 text-ink">
                <Sparkles size={18} className="text-brand" />
                <span className="font-heading font-bold text-lg">Need edge-to-edge printing?</span>
              </div>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed max-w-2xl">
                Please note: our everyday print service is <b>not full-bleed</b> — a thin white border remains around the edge of the paper.
                For professional <b>full-bleed printing</b> (right to the edge) and custom design services, visit our sister site.
              </p>
            </div>
            <a
              href="https://geekcreative.ie"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="geekcreative-link"
              className="inline-flex items-center justify-center gap-2 bg-ink hover:bg-black text-white font-semibold px-6 py-3.5 rounded-lg transition-colors shrink-0"
            >
              Visit GeekCreative.ie <ExternalLink size={16} />
            </a>
          </div>
        </section>
      </main>
      <footer className="border-t border-slate-200 py-10">
        <div className="max-w-6xl mx-auto px-6 md:px-10 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-slate-500">
          <span>© {new Date().getFullYear()} Borrow A Geek Print Services</span>
          <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-6 text-center sm:text-right">
            <a href="https://maps.google.com/?q=18+Henry+Street,+Kilrush,+County+Clare+V15+CX71" target="_blank" rel="noopener noreferrer" className="hover:text-ink transition-colors" data-testid="footer-address">
              18 Henry Street, Kilrush, County Clare V15 CX71
            </a>
            <a href="tel:+353656712772" className="font-semibold text-ink hover:text-brand transition-colors" data-testid="footer-phone">
              +353 65 671 2772
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
